'use client'

import * as React from 'react'
import type { Editor } from '@tiptap/react'
// --- Icons ---
import { BanIcon } from '@/tiptap/tiptap-icons/ban-icon'
import { ChevronDownIcon } from '@/tiptap/tiptap-icons/chevron-down-icon'
import { HighlighterIcon } from '@/tiptap/tiptap-icons/highlighter-icon'
// --- UI Primitives ---
import type { ButtonProps } from '@/tiptap/tiptap-ui-primitive/button'
import { Button, ButtonGroup } from '@/tiptap/tiptap-ui-primitive/button'
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from '@/tiptap/tiptap-ui-primitive/card'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/tiptap/tiptap-ui-primitive/popover'
import { canColorHighlight } from '@/tiptap/tiptap-ui/color-highlight-button'
import { canColorText } from '@/tiptap/tiptap-ui/color-text-button'
// --- Tiptap UI ---
import type {
  ColorType,
  ColorItem,
  RecentColor,
  UseColorTextPopoverConfig,
} from '@/tiptap/tiptap-ui/color-text-popover'
import {
  useColorTextPopover,
  useRecentColors,
} from '@/tiptap/tiptap-ui/color-text-popover'
// --- Styles ---
import '@/tiptap/tiptap-ui/color-text-popover/color-text-popover.scss'
// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'

const FIXED_COLOR_GROUPS: ColorItem[][] = [
  ['#56C1FF', '#72FDEA', '#88FA4F', '#FFF056', '#FF968D', '#FF95CA'],
  ['#00A1FF', '#15E6CF', '#60D937', '#FFDA31', '#FF644E', '#FF42A1'],
  ['#0076BA', '#00AC8E', '#1FB100', '#FEAE00', '#ED220D', '#D31876'],
  ['#004D80', '#006C65', '#027101', '#F27200', '#B51800', '#970E53'],
  ['#FFFFFF', '#D5D5D5', '#929292', '#5E5E5E', '#000000'],
].map((group) => group.map((value) => ({ label: value, value })))

export interface RenderColorButtonProps extends ButtonProps {
  colorObj: RecentColor
  withLabel?: boolean
  onColorChanged?: ({
    type,
    label,
    value,
  }: {
    type: ColorType
    label: string
    value: string
  }) => void
}

export const RecentColorButton: React.FC<RenderColorButtonProps> = ({
  colorObj,
  withLabel = false,
  onColorChanged,
  ...props
}) => {
  return (
    <Button
      type='button'
      data-style='ghost'
      className='!h-8 !w-8 !p-1'
      tooltip={colorObj.label}
      aria-label={`${colorObj.label} ${colorObj.type} color`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onColorChanged?.(colorObj)}
      {...props}
    >
      <span
        className='h-5 w-5 rounded-[3px] border border-black/15'
        style={{ backgroundColor: colorObj.value }}
      />
      {withLabel && (
        <span className='tiptap-button-text'>{colorObj.label}</span>
      )}
    </Button>
  )
}

export interface ColorGroupProps {
  type: ColorType
  colors: ColorItem[][]
  clearLabel?: string
  onClear?: () => void
  onColorSelected: ({
    type,
    label,
    value,
  }: {
    type: ColorType
    label: string
    value: string
  }) => void
}

export const ColorGroup: React.FC<ColorGroupProps> = ({
  type,
  colors,
  clearLabel,
  onClear,
  onColorSelected,
}) => {
  return colors.map((group, groupIndex) => (
    <ButtonGroup key={`${type}-group-${groupIndex}`} orientation='horizontal'>
      {groupIndex === 0 && clearLabel && onClear && (
        <Button
          type='button'
          data-style='ghost'
          className='!h-8 !w-8 !p-1'
          tooltip={clearLabel}
          aria-label={clearLabel}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClear}
        >
          <BanIcon className='h-5 w-5 text-muted-foreground' />
        </Button>
      )}
      {group.map((color, colorIndex) => {
        return (
          <Button
            key={`${type}-${color.value}-${colorIndex}`}
            type='button'
            data-style='ghost'
            className='!h-8 !w-8 !p-1'
            tooltip={color.label}
            aria-label={`${color.label} ${type} color`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onColorSelected({ type, ...color })}
          >
            <span
              className='h-5 w-5 rounded-[3px] border border-black/15'
              style={{ backgroundColor: color.value }}
            />
          </Button>
        )
      })}
    </ButtonGroup>
  ))
}

interface RecentColorsSectionProps {
  recentColors: RecentColor[]
  onColorSelected: ({
    type,
    label,
    value,
  }: {
    type: ColorType
    label: string
    value: string
  }) => void
}

const RecentColorsSection: React.FC<RecentColorsSectionProps> = ({
  recentColors,
  onColorSelected,
}) => {
  if (recentColors.length === 0) return null

  return (
    <CardItemGroup>
      <CardGroupLabel>Recently used</CardGroupLabel>
      <ButtonGroup orientation='horizontal'>
        {recentColors.map((colorObj) => (
          <RecentColorButton
            key={`recent-${colorObj.type}-${colorObj.value}`}
            colorObj={colorObj}
            onColorChanged={onColorSelected}
          />
        ))}
      </ButtonGroup>
    </CardItemGroup>
  )
}

export interface TextStyleColorPanelProps {
  editor?: Editor | null
  colorType: ColorType
  maxRecentColors?: number
  onApplied?: () => void
  onColorChanged?: ({
    type,
    label,
    value,
  }: {
    type: ColorType
    label: string
    value: string
  }) => void
}

export const TextStyleColorPanel: React.FC<TextStyleColorPanelProps> = ({
  editor: providedEditor,
  colorType,
  maxRecentColors = 3,
  onApplied,
  onColorChanged,
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { recentColors, addRecentColor, isInitialized } =
    useRecentColors(maxRecentColors, colorType)
  const clearLabel =
    colorType === 'text' ? 'Default text color' : 'No highlight'

  const handleColorSelected = React.useCallback(
    ({
      type,
      label,
      value,
    }: {
      type: ColorType
      label: string
      value: string
    }) => {
      if (!editor || type !== colorType) return

      setTimeout(() => {
        if (editor.isDestroyed) return

        const success =
          type === 'text'
            ? editor.chain().focus().setColor(value).run()
            : editor.chain().focus().setHighlight({ color: value }).run()

        if (!success) return
        addRecentColor({ type, label, value })
        onColorChanged?.({ type, label, value })
        onApplied?.()
      }, 0)
    },
    [
      addRecentColor,
      colorType,
      editor,
      onApplied,
      onColorChanged,
    ]
  )

  const handleClear = React.useCallback(() => {
    if (!editor) return

    setTimeout(() => {
      if (editor.isDestroyed) return

      const success =
        colorType === 'text'
          ? editor.chain().focus().unsetColor().run()
          : editor.chain().focus().unsetHighlight().run()

      if (!success) return
      onColorChanged?.({ type: colorType, label: clearLabel, value: '' })
      onApplied?.()
    }, 0)
  }, [clearLabel, colorType, editor, onApplied, onColorChanged])

  return (
    <Card className='w-[264px] !border-0 !shadow-none'>
      <CardBody className='flex w-full flex-col gap-2.5'>
        {isInitialized && recentColors.length > 0 && (
          <RecentColorsSection
            recentColors={recentColors}
            onColorSelected={handleColorSelected}
          />
        )}

        <CardItemGroup>
          <CardGroupLabel>
            {colorType === 'text' ? 'Text color' : 'Highlight color'}
          </CardGroupLabel>
          <ColorGroup
            type={colorType}
            colors={FIXED_COLOR_GROUPS}
            clearLabel={clearLabel}
            onClear={handleClear}
            onColorSelected={handleColorSelected}
          />
        </CardItemGroup>
      </CardBody>
    </Card>
  )
}

export interface ColorTextPopoverProps
  extends Omit<ButtonProps, 'type'>,
    UseColorTextPopoverConfig {
  colorType?: ColorType
  popoverPortal?: boolean
}

/**
 * Color text popover component for Tiptap editors.
 *
 * For custom popover implementations, use the `useColorTextPopover` hook instead.
 */
export const ColorTextPopover = React.forwardRef<
  HTMLButtonElement,
  ColorTextPopoverProps
>(
  (
    {
      editor: providedEditor,
      colorType = 'text',
      hideWhenUnavailable = false,
      onColorChanged,
      popoverPortal = false,
      onClick,
      onMouseDown,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor)
    const [isOpen, setIsOpen] = React.useState(false)
    const {
      isVisible,
      canToggle,
      activeTextStyle,
      activeHighlight,
      handleColorChanged,
      Icon: TextColorIcon,
    } = useColorTextPopover({
      editor,
      hideWhenUnavailable,
      onColorChanged,
    })
    const canApplyColor =
      colorType === 'text' ? canColorText(editor) : canColorHighlight(editor)
    const label = colorType === 'text' ? 'Text color' : 'Highlight color'
    const Icon = colorType === 'text' ? TextColorIcon : HighlighterIcon
    const activeColor =
      colorType === 'text' ? activeTextStyle.color : activeHighlight.color

    if (!isVisible) {
      return null
    }

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open)
    }

    const handleApplied = () => {
      setIsOpen(false)
    }

    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <span className='inline-flex'>
            <Button
              type='button'
              data-style='ghost'
              data-appearance='default'
              role='button'
              aria-label={label}
              tooltip={label}
              disabled={!canToggle || !canApplyColor}
              data-disabled={!canToggle || !canApplyColor}
              onClick={onClick}
              onMouseDown={onMouseDown}
              {...buttonProps}
              ref={ref}
            >
              {children ?? (
                <>
                  <span
                    className='tiptap-button-color-text-popover'
                    data-show-color-frame={
                      colorType === 'highlight' && Boolean(activeColor)
                    }
                    style={
                      colorType === 'highlight' && activeColor
                        ? ({
                            '--active-highlight-color': activeColor,
                          } as React.CSSProperties)
                        : ({} as React.CSSProperties)
                    }
                  >
                    <Icon
                      className='tiptap-button-icon'
                      style={{
                        color: activeColor || undefined,
                      }}
                    />
                  </span>
                  <ChevronDownIcon className='tiptap-button-dropdown-small' />
                </>
              )}
            </Button>
          </span>
        </PopoverTrigger>

        <PopoverContent
          aria-label={`${label} options`}
          side='bottom'
          align='start'
          className='color-text-popover-content w-auto p-2'
          sideOffset={8}
          portal={popoverPortal}
          returnFocus={false}
          initialFocus={-1}
        >
          <TextStyleColorPanel
            editor={editor}
            colorType={colorType}
            onApplied={handleApplied}
            onColorChanged={handleColorChanged}
          />
        </PopoverContent>
      </Popover>
    )
  }
)

ColorTextPopover.displayName = 'ColorTextPopover'

export default ColorTextPopover
